<template>
  <VueFinalModal
    v-model="isOpen"
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-6xl h-[85vh] bg-[#fafbfc] rounded-[40px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
    @close="close"
  >
    <template v-if="game">
      <div class="flex w-full h-full min-h-0">
        <div class="w-full lg:w-[32%] bg-[#1c4848] relative flex flex-col p-8 text-white shrink-0 overflow-hidden">
          <div class="relative z-10 flex items-center gap-3 mb-6 opacity-80">
            <div class="w-8 h-8 rounded-full bg-white/10 border border-white/10 flex items-center justify-center">
              <component :is="previewIcon" class="text-[#ecd79f] w-[18px] h-[18px]" />
            </div>
            <span class="text-xs font-bold tracking-[0.2em] uppercase">Preview</span>
          </div>
          <div class="relative z-10 flex-1 flex items-center justify-center">
            <img v-if="coverUrl" :src="coverUrl" class="max-w-[260px] max-h-[400px] rounded-2xl object-cover shadow-xl" alt="">
          </div>
        </div>

        <div class="flex-1 flex flex-col bg-[#fafbfc] relative overflow-hidden min-w-0">
          <header class="px-8 py-6 border-b border-[#1c4848]/5 flex items-center justify-between bg-white/50 backdrop-blur-sm sticky top-0 z-30">
            <h2 class="text-xl font-bold text-[#1c4848] tracking-tight">Edit Game</h2>
            <div class="flex gap-3">
              <button type="button" class="px-4 py-2 rounded-xl text-xs font-bold text-[#1c4848]/60 hover:text-[#1c4848] hover:bg-[#1c4848]/5 transition-colors" @click="close">Cancel</button>
              <button type="button" :disabled="!canSave" class="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#1c4848] shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" @click="save">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                Save
              </button>
            </div>
          </header>

          <div class="flex-1 overflow-y-auto custom-scrollbar p-8">
            <form class="space-y-8 max-w-3xl mx-auto" @submit.prevent="save">
              <section>
                <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">General</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div class="md:col-span-2">
                    <TextInput v-model="form.name" label="Game Title" placeholder="Title" input-id="edit-game-name" />
                  </div>
                  <div class="md:col-span-2">
                    <TextareaInput v-model="form.description" label="Description" :rows="3" textarea-id="edit-game-desc" />
                  </div>
                  <div>
                    <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Difficulty</label>
                    <DifficultyBadge :difficulty="form.difficulty" editable @update:difficulty="form.difficulty = $event" />
                  </div>
                  <div>
                    <SelectInput v-model="form.languageId" label="Language" select-id="edit-game-language" :options="languageOptions" />
                  </div>
                  <div class="md:col-span-2">
                    <ToggleInput v-model="form.isPremium" title="Premium" description="Mark as premium content" />
                  </div>
                </div>
              </section>

              <section v-if="configFields.length">
                <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Game settings</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <template v-for="field in configFields" :key="field.key">
                    <div v-if="field.type === 'select'">
                      <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">{{ field.label }}</label>
                      <select
                        v-model="gameConfig[field.key]"
                        class="w-full appearance-none bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 text-sm font-medium text-[#1c4848] focus:outline-none focus:border-[#1c4848]/30 focus:ring-4 focus:ring-[#1c4848]/5 shadow-sm cursor-pointer"
                      >
                        <option v-for="opt in field.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                      </select>
                    </div>
                    <div v-else-if="field.type === 'number'">
                      <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">{{ field.label }}</label>
                      <input
                        v-model="gameConfig[field.key]"
                        type="number"
                        :min="field.min"
                        :max="field.max"
                        :step="field.step"
                        class="w-full bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 text-sm"
                      >
                    </div>
                    <div v-else class="md:col-span-2">
                      <TextInput
                        v-model="gameConfig[field.key]"
                        :label="field.label"
                        placeholder=""
                        :input-id="'edit-game-cfg-' + field.key"
                      />
                    </div>
                  </template>
                </div>
              </section>

              <section v-if="isMemoryGame">
                <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Image upload</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <UploadImageSizeSelect
                    v-model="form.memoryCoverUploadSize"
                    select-id="edit-game-memory-cover-size"
                    label="Cover image size"
                    hint="Applies when you replace the cover."
                  />
                  <UploadImageSizeSelect
                    v-model="form.memoryPairsUploadSize"
                    select-id="edit-game-memory-pairs-size"
                    label="Pair card images size"
                    hint="Applies when you replace pair images."
                  />
                </div>
              </section>
              <section v-else>
                <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Image upload</h4>
                <UploadImageSizeSelect v-model="form.uploadSize" select-id="edit-game-upload-size" />
              </section>

              <section>
                <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Cover</h4>
                <div class="w-full h-40">
                <ImageDropzone v-model="form.coverFile" input-id="edit-game-cover" size="small" :initial-preview-url="game.cover?.url" class="h-full w-full" />
              </div>
              </section>

              <section v-if="isMemoryGame && memoryAssets.length">
                <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Pair images</h4>
                <p class="text-[11px] text-[#1c4848]/50 mb-4">Replace any card image; the old file is removed and the new one is uploaded when you save.</p>
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div v-for="(asset, i) in memoryAssets" :key="asset.id ?? i" class="min-w-0">
                    <ImageDropzone
                      :model-value="assetSlotFiles[i]"
                      size="small"
                      :input-id="'edit-game-memory-asset-' + (asset.id ?? i)"
                      :initial-preview-url="asset.url"
                      class="h-36 w-full"
                      @update:model-value="(v) => setAssetSlot(i, v)"
                    />
                  </div>
                </div>
              </section>

              <section v-if="isSlidingPuzzle && puzzleAsset">
                <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Puzzle image</h4>
                <div class="w-full max-w-sm h-40">
                  <ImageDropzone
                    v-model="puzzleAssetFile"
                    input-id="edit-game-sliding-asset"
                    size="small"
                    :initial-preview-url="puzzleAsset.url"
                    class="h-full w-full"
                  />
                </div>
              </section>
            </form>
          </div>
        </div>
      </div>
    </template>
  </VueFinalModal>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import TextInput from '@/components/forms/text/TextInput.vue';
import TextareaInput from '@/components/forms/textarea/TextareaInput.vue';
import ImageDropzone from '@/components/forms/dropzone/ImageDropzone.vue';
import SelectInput from '@/components/forms/select/SelectInput.vue';
import UploadImageSizeSelect from '@/components/forms/select/UploadImageSizeSelect.vue';
import DifficultyBadge from '@/components/app/dashboard/DifficultyBadge.vue';
import ToggleInput from '@/components/forms/toggle/ToggleInput.vue';
import IconMemoryGame from '@/components/app/dashboard/games/icons/IconMemoryGame.vue';
import IconSlidingPuzzle from '@/components/app/dashboard/games/icons/IconSlidingPuzzle.vue';
import IconCatchStar from '@/components/app/dashboard/games/icons/IconCatchStar.vue';
import IconColorMatch from '@/components/app/dashboard/games/icons/IconColorMatch.vue';
import IconBubbleShooter from '@/components/app/dashboard/games/icons/IconBubbleShooter.vue';
import IconBlockStack from '@/components/app/dashboard/games/icons/IconBlockStack.vue';
import { useGamesStore } from '@/store/games/index.js';
import { useLanguagesStore } from '@/store/languages/index.js';
import { getLanguageLabel } from '@/utils/languageLabel.js';
import { getGameConfigFields, seedGameConfigValues, buildConfigForSave } from '@/utils/gameConfigEditSchema.js';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  game: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue', 'close', 'saved']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const gamesStore = useGamesStore();
const languagesStore = useLanguagesStore();

const form = reactive({
  name: '',
  description: '',
  difficulty: 1,
  isPremium: false,
  languageId: '',
  uploadSize: '',
  memoryCoverUploadSize: '',
  memoryPairsUploadSize: '',
  coverFile: null,
});

const gameConfig = reactive({});

const assetSlotFiles = ref([]);
const puzzleAssetFile = ref(null);

const isMemoryGame = computed(() => props.game?.game_type === 'match_pairs');
const isSlidingPuzzle = computed(() => props.game?.game_type === 'sliding_puzzle');
const memoryAssets = computed(() => (isMemoryGame.value ? (props.game?.assets || []) : []));
const puzzleAsset = computed(() => (isSlidingPuzzle.value ? (props.game?.assets || [])[0] : null));

function setAssetSlot(i, file) {
  const next = [...assetSlotFiles.value];
  next[i] = file;
  assetSlotFiles.value = next;
}

const languageOptions = computed(() => {
  const list = languagesStore.languages ?? [];
  return [{ value: '', label: 'Select language' }, ...list.map((l) => ({ value: String(l.id), label: getLanguageLabel(l) }))];
});

const game = computed(() => props.game);

const configFields = computed(() => getGameConfigFields(props.game?.game_type));

function resetGameConfigFromGame(g) {
  const seeded = seedGameConfigValues(g.game_type, g.config || {});
  Object.keys(gameConfig).forEach((k) => { delete gameConfig[k]; });
  Object.assign(gameConfig, seeded);
}

watch(() => props.game, async (g) => {
  if (!g) return;
  await languagesStore.fetchLanguages();
  form.name = g.name ?? '';
  form.description = g.description ?? '';
  form.difficulty = g.difficulty != null ? Number(g.difficulty) : 1;
  form.isPremium = g.is_premium ?? g.isPremium ?? false;
  const langId = g.language_id ?? g.languageId ?? g.language?.id;
  form.languageId = langId != null ? String(langId) : '';
  form.uploadSize = '';
  form.memoryCoverUploadSize = '';
  form.memoryPairsUploadSize = '';
  form.coverFile = null;
  puzzleAssetFile.value = null;
  const assets = g.game_type === 'match_pairs' && Array.isArray(g.assets) ? g.assets : [];
  assetSlotFiles.value = assets.map(() => null);
  resetGameConfigFromGame(g);
}, { immediate: true });

const coverUrl = computed(() => {
  if (form.coverFile instanceof File) return URL.createObjectURL(form.coverFile);
  return props.game?.cover?.url ?? '';
});

const canSave = computed(() => Boolean(form.name?.trim()));

const previewIcon = computed(() => {
  const t = props.game?.game_type;
  if (t === 'sliding_puzzle') return IconSlidingPuzzle;
  if (t === 'catch_star') return IconCatchStar;
  if (t === 'color_match') return IconColorMatch;
  if (t === 'bubble_shooter') return IconBubbleShooter;
  if (t === 'block_stack') return IconBlockStack;
  return IconMemoryGame;
});

function close() {
  emit('update:modelValue', false);
  emit('close');
}

async function save() {
  if (!canSave.value || !props.game?.id) return;
  try {
    const configPayload = buildConfigForSave(props.game.game_type, props.game.config, gameConfig);
    await gamesStore.update({
      id: props.game.id,
      name: form.name.trim(),
      description: form.description?.trim() || null,
      difficulty: form.difficulty,
      game_type: props.game.game_type,
      config: configPayload,
      rating: props.game.rating,
      is_premium: form.isPremium,
      language_id: form.languageId ? Number(form.languageId) : null,
    });
    const coverUploadSize = isMemoryGame.value ? form.memoryCoverUploadSize : form.uploadSize;
    if (form.coverFile instanceof File) {
      await gamesStore.uploadCover(props.game.id, form.coverFile, coverUploadSize);
    }

    const memAssets = memoryAssets.value;
    if (isMemoryGame.value && memAssets.length) {
      for (let i = 0; i < memAssets.length; i++) {
        const f = assetSlotFiles.value[i];
        if (!(f instanceof File)) continue;
        const mediaId = memAssets[i]?.id;
        if (mediaId == null) continue;
        await gamesStore.deleteGameFile(props.game.id, mediaId);
        await gamesStore.uploadAssets(props.game.id, [f], false, form.memoryPairsUploadSize);
      }
    }

    if (isSlidingPuzzle.value && puzzleAssetFile.value instanceof File && puzzleAsset.value?.id != null) {
      await gamesStore.deleteGameFile(props.game.id, puzzleAsset.value.id);
      await gamesStore.uploadAssets(props.game.id, [puzzleAssetFile.value], false, form.uploadSize);
    }

    await gamesStore.fetchOne(props.game.id);
    emit('saved');
    close();
  } catch (err) {
    console.error(err);
    alert('Failed to save. Please try again.');
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(28, 72, 72, 0.2); border-radius: 3px; }
</style>
